import React, { useState, useEffect } from 'react';
import { Film, Scissors, Wand2, Sparkles, Music, Check } from 'lucide-react';
import './VideoGenerationLoader.css';

// Processing stages with icons and messages
const PROCESSING_STAGES = [
  {
    id: 'analyze',
    icon: Film,
    title: 'Analysing Footage',
    messages: [
      'Scanning video files...',
      'Reading video metadata...',
      'Detecting frame rates...',
      'Analysing aspect ratios...',
    ]
  },
  {
    id: 'prepare',
    icon: Scissors,
    title: 'Preparing Clips',
    messages: [
      'Trimming to selected ranges...',
      'Extracting key frames...',
      'Optimising clip lengths...',
      'Queueing for processing...',
    ]
  },
  {
    id: 'render',
    icon: Wand2,
    title: 'Rendering Videos',
    messages: [
      'Applying format transformations...',
      'Auto-cropping to subjects...',
      'Rendering transitions...',
      'Encoding video streams...',
    ]
  },
  {
    id: 'effects',
    icon: Sparkles,
    title: 'Applying Effects',
    messages: [
      'Adding crossfade transitions...',
      'Positioning logo watermark...',
      'Colour correcting frames...',
      'Smoothing clip joins...',
    ]
  },
  {
    id: 'audio',
    icon: Music,
    title: 'Mixing Audio',
    messages: [
      'Loading background track...',
      'Syncing audio to video...',
      'Adjusting volume levels...',
      'Finalising audio mix...',
    ]
  },
  {
    id: 'cloud',
    icon: Sparkles,
    title: 'Cloud Rendering',
    messages: [
      'Uploading to cloud renderer...',
      'Processing in the cloud...',
      'Generating download URLs...',
      'Waiting for render to complete...',
    ]
  },
  {
    id: 'finalize',
    icon: Check,
    title: 'Finalising',
    messages: [
      'Compiling final output...',
      'Generating preview URLs...',
      'Almost there...',
      'Nearly ready...',
    ]
  }
];

const VideoGenerationLoader = ({ hasMusic = false, hasStitch = false, clipCount = 1 }) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(0);
  const [stageProgress, setStageProgress] = useState(0);

  // Filter stages based on what's being generated
  const activeStages = PROCESSING_STAGES.filter(stage => {
    if (stage.id === 'audio' && !hasMusic) return false;
    return true;
  });

  useEffect(() => {
    // Message rotation within each stage
    const messageInterval = setInterval(() => {
      setCurrentMessage(prev => {
        const stage = activeStages[currentStage];
        if (!stage) return 0;
        return (prev + 1) % stage.messages.length;
      });
    }, 1800);

    // Stage progress animation
    const progressInterval = setInterval(() => {
      setStageProgress(prev => {
        if (prev >= 100) return 100;
        return prev + Math.random() * 3 + 1;
      });
    }, 200);

    // Move to next stage
    const stageInterval = setInterval(() => {
      setCurrentStage(prev => {
        if (prev < activeStages.length - 1) {
          setStageProgress(0);
          setCurrentMessage(0);
          return prev + 1;
        }
        return prev;
      });
    }, 6000);

    return () => {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
      clearInterval(stageInterval);
    };
  }, [currentStage, activeStages.length]);

  const currentStageData = activeStages[currentStage];
  const CurrentIcon = currentStageData?.icon || Film;
  const overallProgress = ((currentStage / activeStages.length) * 100) + (stageProgress / activeStages.length);

  return (
    <div className="vgl-overlay">
      {/* Animated background elements - outside container for full viewport coverage */}
      <div className="vgl-bg-shapes">
        <div className="vgl-shape vgl-shape-1"></div>
        <div className="vgl-shape vgl-shape-2"></div>
        <div className="vgl-shape vgl-shape-3"></div>
      </div>

      <div className="vgl-container">
        {/* Main content */}
        <div className="vgl-content">
          {/* Circular progress indicator */}
          <div className="vgl-progress-ring-container">
            <svg className="vgl-progress-ring" viewBox="0 0 120 120">
              {/* Background circle */}
              <circle
                className="vgl-ring-bg"
                cx="60"
                cy="60"
                r="54"
                fill="none"
                strokeWidth="6"
              />
              {/* Progress circle */}
              <circle
                className="vgl-ring-progress"
                cx="60"
                cy="60"
                r="54"
                fill="none"
                strokeWidth="6"
                strokeDasharray={339.292}
                strokeDashoffset={339.292 - (339.292 * Math.min(overallProgress, 100)) / 100}
              />
            </svg>
            {/* Center icon */}
            <div className="vgl-center-icon">
              <CurrentIcon size={32} />
            </div>
          </div>

          {/* Stage title */}
          <h2 className="vgl-title">{currentStageData?.title || 'Processing...'}</h2>
          
          {/* Current message */}
          <p className="vgl-message" key={`${currentStage}-${currentMessage}`}>
            {currentStageData?.messages[currentMessage] || 'Please wait...'}
          </p>

          {/* Stage tracker */}
          <div className="vgl-stages">
            {activeStages.map((stage, index) => {
              const StageIcon = stage.icon;
              const isComplete = index < currentStage;
              const isActive = index === currentStage;
              
              return (
                <div 
                  key={stage.id}
                  className={`vgl-stage ${isComplete ? 'complete' : ''} ${isActive ? 'active' : ''}`}
                >
                  <div className="vgl-stage-icon">
                    {isComplete ? <Check size={14} /> : <StageIcon size={14} />}
                  </div>
                  <div className="vgl-stage-line">
                    {isActive && (
                      <div 
                        className="vgl-stage-fill"
                        style={{ width: `${Math.min(stageProgress, 100)}%` }}
                      />
                    )}
                    {isComplete && <div className="vgl-stage-fill" style={{ width: '100%' }} />}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Info badges */}
          <div className="vgl-badges">
            {clipCount > 0 && (
              <span className="vgl-badge">
                <Film size={12} />
                {clipCount} clip{clipCount !== 1 ? 's' : ''}
              </span>
            )}
            {hasStitch && (
              <span className="vgl-badge">
                <Scissors size={12} />
                Stitching enabled
              </span>
            )}
            {hasMusic && (
              <span className="vgl-badge">
                <Music size={12} />
                Background music
              </span>
            )}
          </div>

          {/* Bottom tip */}
          <p className="vgl-tip">
            Sit tight — we're crafting something beautiful
          </p>
        </div>
      </div>
    </div>
  );
};

export default VideoGenerationLoader;
